// useFetchQuote.ts
import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { useClient } from "ClientContext";
import { useBooking } from "context/BookingContext";
import { CLIENT_LISTING_QUOTE_OBJECT } from "data/backEndTypes";
import { QUOTE_ERRORS, QuoteErrorConfig } from "data/errorMessages";
import { useTranslation } from "react-i18next";

export interface Quote {
  currency: string;
  preTotal: number;
  nightlyPrice: number;
  nightlyTotal: number;
  lengthOfStay: number;
  totalPromo?: number;
  promoItems?: {
    name: string;
    value: number;
    type?: string | "PERCENT";
  }[];
  totalFees: number;
  feesItems: Array<{ title: string; amount: number }>;
  totalTaxes: number;
  taxesItems: Array<{ title: string; amount: number }>;
  subTotal: number;
  stayTotal: number;
  guestsCount?: number;
  coupons?: Array<{
    name: string;
    couponCode: string;
    discountType: "PERCENT" | "FLAT";
    discount: number;
  }>;
}
interface FetchQuoteProps {
  couponCode?: string;
  listingId?: string;
  roomId?: string; // only for lodgify quotes
  addons?: Array<{ title: string; amount: number; type: string; currency?: string }>;
}

export function useFetchQuote() {
  const { t } = useTranslation();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [isQuote, setIsQuote] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quoteError, setError] = useState<string | null>(null);
  const [couponErrorState, setCouponErrorState] = useState<string | null>(null);

  const { bookingState, setBookingState } = useBooking();
  const { internal_ID, actions, global_currency,integration_TYPE } = useClient();
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const default_currency = global_currency[0];
  const checkInValue = bookingState?.searchParams?.checkIn;
  const checkOutValue = bookingState?.searchParams?.checkOut;
  const guestsNum = bookingState?.searchParams?.guests || 0;

  const checkin = checkInValue
    ? format(new Date(checkInValue), "yyyy-MM-dd")
    : null;

  const checkout = checkOutValue
    ? format(new Date(checkOutValue), "yyyy-MM-dd")
    : null;
  const handleFetchQuote = async ({ couponCode, listingId, addons, roomId }: FetchQuoteProps): Promise<void> => {
    if (!checkin || !checkout) {
      setError("Please select valid check-in and check-out dates.");
      return;
    }
    if(integration_TYPE==="lodgify" && !roomId){ // lodgify requires roomId for quote fetching
      return;
    }

    if (guestsNum < 1) {
      setError("A minimum of 1 guest is required.");
      return;
    }

    // Reset previous errors and start loading
    setError(null);
    setCouponErrorState("");
    setLoading(true);
    try {
      const response = await axios.post(`${SERVER_URL}/api/controller`, {
        action: actions.fetchListingQuote,
        internal_ID,
        params: {
          listingId,
          roomId: roomId, // Add roomId if applicable, lodgify quote API requires it

          quote: {
            guestsCount: guestsNum,
            checkInDateLocalized: checkin,
            checkOutDateLocalized: checkout,
            currency: default_currency,
            coupons: couponCode || "", // Ensure empty string if no coupon is provided
            addons: addons || [],
          },
        },
      });


      // Handle success response
      if (
        response.status === 200 &&
        response.data.status !== "Failed to fetch quote data"
      ) {
        const quoteData = response.data.item;
        setQuote(quoteData);

        // Update booking state with new quote
        setBookingState((prevState) => ({
          ...prevState,
          quote: {
            quote: quoteData,
          },
        }));

        setIsQuote(true);
      } else {
        // Extract and clean server error message
        const cleanedMessage = response.data.error
          ? response.data.error.replace(/^Error \d+:/, "").trim()
          : "An unknown error occurred.";
        setError(cleanedMessage);
        setIsQuote(false);
      }
    } catch (err) {
      // TypeScript: assert this is an AxiosError
      if (!axios.isAxiosError(err) || !err.response) {
        setError("Unknown error occurred");
        return;
      }

      const { message: rawMsg = "" } = err.response.data as { message?: string };
      const raw = rawMsg.trim();
      console.warn("Server says:", raw);

      // look up a config, or default to echoing raw text
      const config: QuoteErrorConfig = QUOTE_ERRORS[raw] ?? {
        message: raw || "Unknown server error",
      };

      if (config.isCoupon) {
        setCouponErrorState(config.message);
      } else {
        setError(t(config.message));
      }
    } finally {
      setLoading(false);
    }
  };
  return {
    quote,
    isQuote,
    loading,
    quoteError,
    handleFetchQuote,
    setError,
    setIsQuote,
    default_currency,
    checkin,
    checkout,
    guestsNum,
    couponErrorState,
    setCouponErrorState
  };
}
